import React from 'react';
import _get from 'lodash/get';
import _set from 'lodash/set';
import PropTypes from 'prop-types';

import { ChartService } from '../../services/chartService';
import { formatter, getStateName } from '../../utils';
import LineChart from '../lineChart';

import LocationFilter from './LocationFilter';

const SALARY_LOCATION_COLORS = [
  { borderColor: '#26A69A', pointColor: '#26A69A' },
  { borderColor: '#5C6BC0', pointColor: '#5C6BC0' },
  { borderColor: '#AB47BC', pointColor: '#AB47BC' },
  { borderColor: '#FFA726', pointColor: '#FFA726' },
];

const DATASETS_PROPS = {
  borderWidth: 2,
  pointRadius: 0,
  hitRadius: 3,
  hoverRadius: 3,
  hoverBorderWidth: 1,
  backgroundColor: 'rgba(0, 0, 0, 0)',
};

function SalaryHistoryChartWithLocationFilter(props) {
  const {
    contentBlurb,
    titleKey,
    titleValue,
    initialLocation,
    nationalSalaries,
    salariesByStateAndCity,
    noLocationFilter,
    callFunc,
  } = props;

  function getInitialLabels() {
    const initialLabels = nationalSalaries.map((salaryData) => _get(salaryData, 'label'));
    return initialLabels;
  }

  const labels = getInitialLabels();

  function getInitialOptions() {
    if (window.innerWidth < 768) {
      return { maxTicksLimit: 6, xMin: labels[1] };
    } else {
      return { maxTicksLimit: 11, xMin: labels[0] };
    }
  }

  const [datasets, setDatasets] = React.useState([]);

  const { maxTicksLimit, xMin } = getInitialOptions();

  function getInitialDatasets() {
    const nationalSalaryValues = nationalSalaries.map((salaryData) => salaryData.value);
    const ipGeolocatedCitySalaryValues = salariesByStateAndCity.length
      ? salariesByStateAndCity.map((salaryData) => salaryData.value)
      : [];
    const initialDatasets = [];
    const { state, city, stateAbbr } =
      initialLocation && initialLocation.length && initialLocation[0];
    initialDatasets.push({
      data: nationalSalaryValues,
      label: 'National Average',
      datasetKeyProvider: 'National Average',
      borderColor: SALARY_LOCATION_COLORS[0].borderColor,
      pointBackgroundColor: SALARY_LOCATION_COLORS[0].pointColor,
      ...DATASETS_PROPS,
    });
    if (state || city) {
      initialDatasets.push({
        data: ipGeolocatedCitySalaryValues,
        label: `${city ? `${city}, ${stateAbbr}` : state}`,
        datasetKeyProvider: `${city ? `${city}, ${stateAbbr}` : state}`,
        borderColor: SALARY_LOCATION_COLORS[1].borderColor,
        pointBackgroundColor: SALARY_LOCATION_COLORS[1].pointColor,
        ...DATASETS_PROPS,
      });
    }
    return initialDatasets;
  }

  const preselectedFilterLocation = (() => {
    const { state = '', city = '', stateAbbr = '' } = initialLocation[0] || {};

    if (!state && !stateAbbr) return [];

    return [
      {
        value: { city, state },
        displayName: city ? `${city}, ${stateAbbr}` : state,
      },
    ]
  })();

  React.useEffect(() => {
    setDatasets(getInitialDatasets());
  }, [salariesByStateAndCity]);

  async function fetchSalariesForLocations(queryParams, stateDisplayName, index) {
    try {
      const { city, state } = queryParams;
      const response = await callFunc(queryParams);
      const salariesForNewLocation = response.map((salary) => salary.value);
      const label = city ? `${city}, ${state}` : stateDisplayName;
      const newDataSet = {
        data: salariesForNewLocation,
        label,
        datasetKeyProvider: label,
        borderColor: SALARY_LOCATION_COLORS[index + 1].borderColor,
        pointBackgroundColor: SALARY_LOCATION_COLORS[index + 1].pointColor,
        ...DATASETS_PROPS,
      };
      return newDataSet;
    } catch (err) {
      console.error('Error occurred:', err);
      throw err;
    }
  }

  async function updateFilter(_, data) {
    try {
      // Fetch datasets for every location and render them to the chart
      const newDatasetsPromise = data.map(({ value }, index) => {
        const { city, state } = value;
        const stateDisplayName = getStateName(state);
        const queryParams = {
          city,
          state,
          titleKey,
          titleValue,
        };
        return fetchSalariesForLocations(queryParams, stateDisplayName, index);
      });
      Promise.all(newDatasetsPromise)
        .then((newDatasets) => {
          setDatasets([datasets[0], ...newDatasets]);
        })
        .catch((err) => {
          console.error('Error occurred while fetching datasets for salary history chart.', err);
        });
    } catch (err) {
      console.error('Error occurred while fetching datasets for salary history chart.', err);
    }
  }

  function handleHistoryChartResize(chartInstance) {
    const { maxTicksLimit, xMin } = getInitialOptions();
    _set(chartInstance, 'options.scales.yAxes[0].ticks.maxTicksLimit', maxTicksLimit);
    _set(chartInstance, 'options.scales.xAxes[0].ticks.maxTicksLimit', maxTicksLimit + 1);
    _set(chartInstance, 'options.scales.xAxes[0].ticks.min', xMin);
  }

  const lineChartProps = () => {
    return ChartService.setupLineChart({
      labels,
      datasets,
      options: {
        onResize: handleHistoryChartResize,
        showCustomLegend: true,
        tooltips: {
          callbacks: {
            label(tooltipItem, data) {
              const { value, datasetIndex } = tooltipItem;
              return `${data.datasets[datasetIndex].label}: <strong>${formatter(false).format(
                value
              )}</strong>`;
            },
          },
        },
        scales: {
          yAxes: [
            {
              ticks: {
                maxTicksLimit,
              },
            },
          ],
          xAxes: [
            {
              ticks: {
                xMin,
                maxTicksLimit,
              },
            },
          ],
        },
      },
      className: 'salary-line-chart--history',
    });
  };

  return (
    <>
      <div className="mb-24">
        <LocationFilter
          enableFilter
          onUpdateFilter={updateFilter}
          onFilterClear={() => setDatasets([datasets[0]])}
          title={contentBlurb || `Compare ${titleValue?.toLowerCase?.()} salaries for individual cities or states with the national average.`}
          noLocationFilter={noLocationFilter}
          preselectedItems={preselectedFilterLocation}
        />
      </div>

      <LineChart {...lineChartProps()} />
    </>
  );
}

SalaryHistoryChartWithLocationFilter.propTypes = {
  searchLocations: PropTypes.array,
  initialLocation: PropTypes.any,
  titleKey: PropTypes.string,
  titleValue: PropTypes.string,
  contentBlurb: PropTypes.string,
  noLocationFilter: PropTypes.bool,
  nationalSalaries: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      label: PropTypes.string,
    })
  ).isRequired,
  salariesByStateAndCity: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      label: PropTypes.string,
    })
  ).isRequired,
  callFunc: PropTypes.func,
};

export default SalaryHistoryChartWithLocationFilter;
